import type {
  PublishListingVisibilityBucket,
  PublishObservabilitySummary,
  PublishObservedStage,
  PublishStageSummary,
  PublishVehicleDataBucket,
  PublishVehicleGroupBucket,
} from '@/services/publishPreviewArtifactsService'

export type PublishPreviewWarning = {
  severity: 'info' | 'warning' | 'danger'
  category:
    | 'inventoryVisibility'
    | 'vehicleGroupings'
    | 'vehicleInformation'
    | 'previewUnsupported'
  title: string
  message: string
  affectedCount?: number
  affectedPercent?: number
  vinSample?: string[]
}

export type PublishImpactPreviewViewModel = {
  updatedAt?: string | null
  impactSummary: {
    inventoryVisibility: InventoryVisibilityImpact
    vehicleGroupings: VehicleGroupingImpact
    vehicleInformation: VehicleInformationImpact
  }
  /**
   * Unified website-impact dataset: the five Publish-Flow-equivalent categories
   * with predictive meaning. All backed by the SAME data (`summary.websiteImpact`)
   * that also serves the drilldown VIN lists, so the Preview Summary, Website
   * Publish Outcome stage, and View VINs modal never disagree.
   */
  websiteImpact: WebsiteImpactSummaryViewModel
  previewFlow: {
    inventoryEligibility: InventoryEligibilityFlow
    vehicleInformation: VehicleInformationFlow
    vehicleGroupings: VehicleGroupingFlow
    websiteVisibility: WebsiteVisibilityFlow
  }
  warnings: PublishPreviewWarning[]
}

export type WebsiteImpactCategory =
  | 'addedToWebsite'
  | 'removedFromWebsite'
  | 'updatedOnWebsite'
  | 'madeVisible'
  | 'hidden'

/** Per-scope counts for the five website-impact categories (binary origin split). */
export type WebsiteImpactScopeCountsViewModel = {
  addedToWebsite: number
  removedFromWebsite: number
  updatedOnWebsite: number
  madeVisible: number
  hidden: number
}

export type WebsiteImpactSummaryViewModel = {
  available: boolean
  addedToWebsite: number
  removedFromWebsite: number
  updatedOnWebsite: number
  madeVisible: number
  hidden: number
  /** Deduped union of VINs changing live/website state across all five categories. */
  changedLiveVinCount: number
  /**
   * True when the artifact carries the binary Feed-applied / Manual split.
   * Derived purely from PRESENCE of the scoped fields (not a scope
   * classification and never `unknown`). Legacy artifacts leave this false so
   * the UI can hide the split instead of showing zeros as authoritative.
   */
  splitAvailable: boolean
  /** Binary origin split; zeroed (and not authoritative) when `splitAvailable` is false. */
  feedApplied: WebsiteImpactScopeCountsViewModel
  manual: WebsiteImpactScopeCountsViewModel
}

export type InventoryVisibilityImpact = {
  available: boolean
  bucketDetailsAvailable: boolean
  manualOnly: boolean
  manualOnlyHiddenCount: number
  addedOnline: number
  removedOnline: number
  unchangedVisibility: number
  visibleAfterPublish?: number
  hiddenAfterPublish?: number
  totalEvaluated: number
  affectedVins: string[]
  warning?: PublishPreviewWarning | null
}

export type VehicleGroupingImpact = {
  available: boolean
  fallbackGroupAvailable: boolean
  changed: number
  addedToGroups: number
  removedFromGroups: number
  removedFromInventoryGroupings: number
  affectedGroupingCount: number
  affectedGroupings: Array<{
    groupName: string
    removedVehicleCount: number
  }>
  affectedGroupNames: string[]
  fallbackGroupCount: number
  unchanged: number
  totalEvaluated: number
  affectedVins: string[]
  warning?: PublishPreviewWarning | null
}

export type VehicleInformationImpact = {
  available: boolean
  valueLossAvailable: boolean
  changedVehicles: number
  vehiclesLosingValues: number
  changedFields: number
  totalEvaluated: number
  affectedVins: string[]
  warning?: PublishPreviewWarning | null
}

export type InventoryEligibilityFlow = {
  inputCount: number
  continueCount: number
  removedCount?: number
  unchanged?: boolean
  summaryText: string
}

export type VehicleInformationFlow = {
  changed: number
  unchanged: number
  affectedVins: string[]
  summaryText: string
}

export type VehicleGroupingFlow = {
  changed: number
  unchanged: number
  affectedVins: string[]
  fallbackGroupCount: number
  summaryText: string
}

export type WebsiteVisibilityFlow = {
  changed: number
  unchanged: number
  addedOnline: number
  removedOnline: number
  visibleAfterPublish?: number
  hiddenAfterPublish?: number
  affectedVins: string[]
  summaryText: string
}

type PublishObservabilitySummaryWithLegacyImpact = PublishObservabilitySummary & {
  impact?: {
    byStage?: {
      groups?: {
        vinsChanged?: number
        vinsAdded?: number
        vinsRemoved?: number
        vinsUnchanged?: number
        vinsErrored?: number
        vinsSkipped?: number
      }
    }
  }
  vehicleGroupBuckets?: PublishObservabilitySummary['vehicleGroupBuckets'] & {
    fallbackGroup?: number
    fallbackGroupCount?: number
    fallback?: number
  }
  vehicleGroupingImpact?: {
    fallbackGroupCount?: number
    fallbackGroupVins?: string[]
  }
}

export function buildPublishImpactPreviewViewModel(
  rawPreview: PublishObservabilitySummary | null | undefined,
): PublishImpactPreviewViewModel {
  const impactSummary = {
    inventoryVisibility: getInventoryVisibilityImpact(rawPreview),
    vehicleGroupings: getVehicleGroupingImpact(rawPreview),
    vehicleInformation: getVehicleInformationImpact(rawPreview),
  }

  const viewModel: PublishImpactPreviewViewModel = {
    updatedAt: rawPreview?.generatedAt ?? null,
    impactSummary,
    websiteImpact: getWebsiteImpact(rawPreview),
    previewFlow: getPreviewFlow(rawPreview, impactSummary),
    warnings: [],
  }

  viewModel.warnings = getPreviewWarnings(viewModel)

  return viewModel
}

/**
 * Unified website-impact selector — the single source for the five
 * Publish-Flow-equivalent categories.
 *
 * Prefers `summary.websiteImpact` (emitted by the backend, and the same data
 * that backs the drilldown VIN lists). Falls back to
 * `listingVisibilityInventoryDelta` for older artifacts that predate the
 * `websiteImpact` field. In the fallback path `updatedOnWebsite` is reported as
 * 0 because "still-live AND website-data-changed" cannot be computed strictly
 * without the per-VIN records — we never approximate it from raw field-mapping
 * change counts (see plan guardrail 2).
 */
export function getWebsiteImpact(
  rawPreview: PublishObservabilitySummary | null | undefined,
): WebsiteImpactSummaryViewModel {
  const direct = rawPreview?.websiteImpact
  if (direct) {
    // `splitAvailable` is a PRESENCE check on the scoped fields — never a scope
    // classification and never `unknown`. Current artifacts carry both scopes;
    // legacy artifacts omit them, so we zero the split and mark it unavailable.
    const hasSplit = Boolean(direct.feedApplied && direct.manual)
    return {
      available: true,
      addedToWebsite: direct.addedToWebsite ?? 0,
      removedFromWebsite: direct.removedFromWebsite ?? 0,
      updatedOnWebsite: direct.updatedOnWebsite ?? 0,
      madeVisible: direct.madeVisible ?? 0,
      hidden: direct.hidden ?? 0,
      changedLiveVinCount: direct.changedLiveVinCount ?? 0,
      splitAvailable: hasSplit,
      feedApplied: toScopeCounts(direct.feedApplied),
      manual: toScopeCounts(direct.manual),
    }
  }

  const delta = rawPreview?.listingVisibilityInventoryDelta
  if (delta) {
    return {
      available: true,
      addedToWebsite: delta.addedToInventory ?? 0,
      removedFromWebsite: delta.removedFromInventory ?? 0,
      updatedOnWebsite: 0,
      madeVisible: delta.madeVisible ?? 0,
      hidden: delta.hidden ?? 0,
      changedLiveVinCount: 0,
      splitAvailable: false,
      feedApplied: toScopeCounts(undefined),
      manual: toScopeCounts(undefined),
    }
  }

  return {
    available: false,
    addedToWebsite: 0,
    removedFromWebsite: 0,
    updatedOnWebsite: 0,
    madeVisible: 0,
    hidden: 0,
    changedLiveVinCount: 0,
    splitAvailable: false,
    feedApplied: toScopeCounts(undefined),
    manual: toScopeCounts(undefined),
  }
}

function toScopeCounts(
  scope:
    | {
        addedToWebsite?: number
        removedFromWebsite?: number
        updatedOnWebsite?: number
        madeVisible?: number
        hidden?: number
      }
    | undefined
    | null,
): WebsiteImpactScopeCountsViewModel {
  return {
    addedToWebsite: scope?.addedToWebsite ?? 0,
    removedFromWebsite: scope?.removedFromWebsite ?? 0,
    updatedOnWebsite: scope?.updatedOnWebsite ?? 0,
    madeVisible: scope?.madeVisible ?? 0,
    hidden: scope?.hidden ?? 0,
  }
}

export function getInventoryVisibilityImpact(
  rawPreview: PublishObservabilitySummary | null | undefined,
): InventoryVisibilityImpact {
  const stage = availableStage(rawPreview, 'listingRules')
  const buckets = rawPreview?.listingVisibilityBuckets
  const outcome = rawPreview?.listingVisibilityOutcome
  const inventoryDelta = rawPreview?.listingVisibilityInventoryDelta
  const bucketDetailsAvailable = !!buckets
  const manualOnlyHiddenCount = manualOnlyReconciliationHiddenCount(rawPreview)
  const manualOnly = manualOnlyHiddenCount > 0 || isManualOnlyPreview(rawPreview)
  const totalEvaluated = inventoryDelta?.evaluated ?? outcome?.evaluated ?? stage?.counts.evaluated ?? 0
  const removedOnline = inventoryDelta?.hidden ?? (manualOnlyHiddenCount > 0 ? manualOnlyHiddenCount : buckets?.willHide ?? 0)
  const addedOnline = inventoryDelta?.madeVisible ?? (manualOnlyHiddenCount > 0 ? 0 : buckets?.willAppear ?? 0)
  const unchangedVisibility = inventoryDelta?.unchangedVisibility ?? buckets?.unchanged ?? stage?.counts.unchanged ?? 0

  const impact: InventoryVisibilityImpact = {
    available: !!stage,
    bucketDetailsAvailable,
    manualOnly,
    manualOnlyHiddenCount,
    addedOnline,
    removedOnline,
    unchangedVisibility,
    visibleAfterPublish: outcome?.visibleAfterPublish,
    hiddenAfterPublish: outcome?.hiddenAfterPublish,
    totalEvaluated,
    affectedVins: [],
    warning: null,
  }

  impact.warning = visibilityRemovalWarning(impact)
  return impact
}

export function getVehicleGroupingImpact(
  rawPreview: PublishObservabilitySummary | null | undefined,
): VehicleGroupingImpact {
  const summary = rawPreview as PublishObservabilitySummaryWithLegacyImpact | null | undefined
  const stage = availableStage(rawPreview, 'groups')
  const counts = stage?.counts
  const buckets = summary?.vehicleGroupBuckets
  const inventoryDelta = rawPreview?.vehicleGroupingInventoryDelta
  const legacy = summary?.impact?.byStage?.groups
  const removedFromInventoryGroupings = inventoryDelta?.removedVehicleCount ?? 0
  const fallbackGroupCount =
    buckets?.fallbackGroupCount
    ?? buckets?.fallbackGroup
    ?? buckets?.fallback
    ?? summary?.vehicleGroupingImpact?.fallbackGroupCount
    ?? 0

  const impact: VehicleGroupingImpact = {
    available: !!stage,
    fallbackGroupAvailable: fallbackGroupCount > 0 || hasFallbackGroupMetric(summary),
    changed: buckets?.changed ?? legacy?.vinsChanged ?? counts?.changed ?? 0,
    addedToGroups: buckets?.added ?? legacy?.vinsAdded ?? 0,
    removedFromGroups: buckets?.removed ?? legacy?.vinsRemoved ?? 0,
    removedFromInventoryGroupings,
    affectedGroupingCount: inventoryDelta?.affectedGroupingCount ?? 0,
    affectedGroupings: (inventoryDelta?.affectedGroupings ?? []).map((group) => ({
      groupName: group.groupName,
      removedVehicleCount: group.removedVehicleCount,
    })),
    affectedGroupNames: (inventoryDelta?.affectedGroupings ?? []).map((group) => group.groupName),
    fallbackGroupCount,
    unchanged: buckets?.unchanged ?? legacy?.vinsUnchanged ?? counts?.unchanged ?? 0,
    totalEvaluated: counts?.evaluated ?? 0,
    affectedVins: summary?.vehicleGroupingImpact?.fallbackGroupVins ?? [],
    warning: null,
  }

  if (impact.fallbackGroupCount > 0) {
    impact.warning = {
      severity: 'warning',
      category: 'vehicleGroupings',
      title: 'Fallback group',
      message: `${impact.fallbackGroupCount} ${pluralize(impact.fallbackGroupCount, 'vehicle')} will fall back to All.`,
      affectedCount: impact.fallbackGroupCount,
      vinSample: impact.affectedVins.slice(0, 5),
    }
  }

  return impact
}

export function getVehicleInformationImpact(
  rawPreview: PublishObservabilitySummary | null | undefined,
): VehicleInformationImpact {
  const stage = availableStage(rawPreview, 'fieldMapping')
  const audit = rawPreview?.auditSummaries?.fieldMapping
  const dataBuckets = audit?.vehicleDataBuckets
  const vehiclesLosingValues = dataBuckets?.vehiclesWithValuesBecomingEmpty ?? 0
  const changedVehicles = dataBuckets?.changedVehicles ?? audit?.changedVehicles ?? stage?.counts.changed ?? 0
  const changedFields = (audit?.fieldSummaries ?? []).reduce(
    (sum, field) => sum + Math.max(0, field.valueChanges ?? 0),
    0,
  )
  const affectedVins = uniqueStrings([
    ...(audit?.valueChangedSamples ?? []),
    ...(audit?.sourceOnlySamples ?? []),
    ...(audit?.auditIntegritySamples ?? []),
  ])

  const impact: VehicleInformationImpact = {
    available: !!stage,
    valueLossAvailable: !!dataBuckets,
    changedVehicles,
    vehiclesLosingValues,
    changedFields,
    totalEvaluated: stage?.counts.evaluated ?? audit?.changedVehicles ?? 0,
    affectedVins,
    warning: null,
  }

  if (vehiclesLosingValues > 0) {
    const allVehiclesLoseValues =
      impact.totalEvaluated > 0 && vehiclesLosingValues === impact.totalEvaluated
    impact.warning = {
      severity: allVehiclesLoseValues ? 'danger' : 'warning',
      category: 'vehicleInformation',
      title: 'Values removed',
      message: `${vehiclesLosingValues} ${pluralize(vehiclesLosingValues, 'vehicle')} will lose one or more website values after publish.`,
      affectedCount: vehiclesLosingValues,
      vinSample: affectedVins.slice(0, 5),
    }
  }

  return impact
}

export function getPreviewFlow(
  rawPreview: PublishObservabilitySummary | null | undefined,
  impactSummary?: PublishImpactPreviewViewModel['impactSummary'],
): PublishImpactPreviewViewModel['previewFlow'] {
  const summary = impactSummary ?? {
    inventoryVisibility: getInventoryVisibilityImpact(rawPreview),
    vehicleGroupings: getVehicleGroupingImpact(rawPreview),
    vehicleInformation: getVehicleInformationImpact(rawPreview),
  }
  const preProcessor = availableStage(rawPreview, 'preProcessor')
  const manualOnly = isManualOnlyPreview(rawPreview)
  const inventoryInput = manualOnly
    ? 0
    : preProcessor?.diagnostics
    ? inputUniqueVinCount(preProcessor.diagnostics)
    : preProcessor?.counts.evaluated ?? 0
  const inventoryContinue = manualOnly
    ? 0
    : preProcessor?.diagnostics
    ? outputUniqueVinCount(preProcessor.diagnostics)
    : preProcessor?.counts.unchanged ?? inventoryInput
  const removedCount = Math.max(0, inventoryInput - inventoryContinue)
  const visibilityBuckets = rawPreview?.listingVisibilityBuckets
  const visibilityStage = availableStage(rawPreview, 'listingRules')
  const flowAddedOnline = visibilityBuckets?.willAppear ?? 0
  const flowRemovedOnline = visibilityBuckets?.willHide ?? 0
  const flowUnchangedVisibility = visibilityBuckets?.unchanged ?? visibilityStage?.counts.unchanged ?? 0

  return {
    inventoryEligibility: {
      inputCount: inventoryInput,
      continueCount: inventoryContinue,
      removedCount,
      unchanged: removedCount === 0,
      summaryText: manualOnly
        ? 'Inventory pre-processing is skipped because publish will run in manual-only mode.'
        : `${inventoryContinue} ${pluralize(inventoryContinue, 'vehicle')} will continue after inventory eligibility.`,
    },
    vehicleInformation: {
      changed: summary.vehicleInformation.changedVehicles,
      unchanged: availableStage(rawPreview, 'fieldMapping')?.counts.unchanged ?? 0,
      affectedVins: summary.vehicleInformation.affectedVins,
      summaryText: `${summary.vehicleInformation.changedVehicles} changed, ${
        availableStage(rawPreview, 'fieldMapping')?.counts.unchanged ?? 0
      } unchanged.`,
    },
    vehicleGroupings: {
      changed: summary.vehicleGroupings.changed,
      unchanged: summary.vehicleGroupings.unchanged,
      affectedVins: summary.vehicleGroupings.affectedVins,
      fallbackGroupCount: summary.vehicleGroupings.fallbackGroupCount,
      summaryText: `${summary.vehicleGroupings.changed} grouping changes, ${summary.vehicleGroupings.unchanged} unchanged.`,
    },
    websiteVisibility: {
      changed: visibilityBuckets?.changed ?? visibilityStage?.counts.changed ?? 0,
      unchanged: flowUnchangedVisibility,
      addedOnline: flowAddedOnline,
      removedOnline: flowRemovedOnline,
      visibleAfterPublish: summary.inventoryVisibility.visibleAfterPublish,
      hiddenAfterPublish: summary.inventoryVisibility.hiddenAfterPublish,
      affectedVins: summary.inventoryVisibility.affectedVins,
      summaryText: websiteVisibilitySummaryText(summary.inventoryVisibility),
    },
  }
}

export function getPreviewWarnings(
  viewModel: PublishImpactPreviewViewModel,
): PublishPreviewWarning[] {
  return [
    viewModel.impactSummary.inventoryVisibility.warning,
    viewModel.impactSummary.vehicleGroupings.warning,
    viewModel.impactSummary.vehicleInformation.warning,
  ].filter((warning): warning is PublishPreviewWarning => !!warning)
}

const WEBSITE_IMPACT_BUCKETS: WebsiteImpactCategory[] = [
  'addedToWebsite',
  'removedFromWebsite',
  'updatedOnWebsite',
  'madeVisible',
  'hidden',
]

function isWebsiteImpactBucket(
  bucket: PublishListingVisibilityBucket,
): bucket is WebsiteImpactCategory {
  return (WEBSITE_IMPACT_BUCKETS as string[]).includes(bucket)
}

export function isVisibilityBucketAvailable(
  rawPreview: PublishObservabilitySummary | null | undefined,
  bucket: PublishListingVisibilityBucket,
): boolean {
  if (isWebsiteImpactBucket(bucket)) {
    return rawPreview?.websiteImpact?.[bucket] !== undefined
  }
  return rawPreview?.listingVisibilityBuckets?.[bucket] !== undefined
}

/**
 * Count for a listing-visibility OR website-impact bucket, read from the same
 * summary that backs the drilldown lists. Website-impact counts come from the
 * unified `websiteImpact` dataset so Summary / Outcome / modal always agree.
 */
export function visibilityBucketCount(
  rawPreview: PublishObservabilitySummary | null | undefined,
  bucket: PublishListingVisibilityBucket,
): number {
  if (isWebsiteImpactBucket(bucket)) {
    return rawPreview?.websiteImpact?.[bucket] ?? 0
  }
  return rawPreview?.listingVisibilityBuckets?.[bucket] ?? 0
}

export function isVehicleDataBucketAvailable(
  rawPreview: PublishObservabilitySummary | null | undefined,
  bucket: PublishVehicleDataBucket,
): boolean {
  const buckets = rawPreview?.auditSummaries?.fieldMapping?.vehicleDataBuckets
  if (!buckets) return false
  if (bucket === 'changed') return buckets.changedVehicles !== undefined
  if (bucket === 'unchanged') return buckets.unchangedVehicles !== undefined
  return buckets.vehiclesWithValuesBecomingEmpty !== undefined
}

export function isVehicleGroupBucketAvailable(
  rawPreview: PublishObservabilitySummary | null | undefined,
  bucket: PublishVehicleGroupBucket,
): boolean {
  return rawPreview?.vehicleGroupBuckets?.[bucket] !== undefined
}

function availableStage(
  rawPreview: PublishObservabilitySummary | null | undefined,
  stage: PublishObservedStage,
): PublishStageSummary | null {
  const stageSummary = rawPreview?.stages?.[stage]
  return stageSummary?.availability === 'available' ? stageSummary : null
}

function visibilityRemovalWarning(
  impact: InventoryVisibilityImpact,
): PublishPreviewWarning | null {
  if (impact.totalEvaluated <= 0) {
    return null
  }

  const hiddenCount = impact.removedOnline
  if (hiddenCount <= 0) {
    return null
  }

  const percent = Math.round((hiddenCount / impact.totalEvaluated) * 100)
  if (hiddenCount === impact.totalEvaluated) {
    return {
      severity: 'danger',
      category: 'inventoryVisibility',
      title: 'All vehicles hidden',
      message: `All vehicles will be hidden by this publish.`,
      affectedCount: hiddenCount,
      affectedPercent: percent,
    }
  }

  if (percent >= 25) {
    return {
      severity: 'danger',
      category: 'inventoryVisibility',
      title: 'Large visibility change',
      message: `Large visibility change: ${hiddenCount} ${pluralize(hiddenCount, 'vehicle')}, ${percent}% of inventory, will be hidden after publish.`,
      affectedCount: hiddenCount,
      affectedPercent: percent,
    }
  }

  if (percent >= 10) {
    return {
      severity: 'warning',
      category: 'inventoryVisibility',
      title: 'Vehicles hidden',
      message: `${hiddenCount} ${pluralize(hiddenCount, 'vehicle')} will be hidden after publish.`,
      affectedCount: hiddenCount,
      affectedPercent: percent,
    }
  }

  return null
}

function websiteVisibilitySummaryText(impact: InventoryVisibilityImpact): string {
  if (impact.manualOnly && impact.manualOnlyHiddenCount > 0) {
    return `${impact.manualOnlyHiddenCount} feed-backed ${pluralize(impact.manualOnlyHiddenCount, 'vehicle')} will be hidden because publish will run in manual-only mode.`
  }

  if (impact.visibleAfterPublish !== undefined && impact.hiddenAfterPublish !== undefined) {
    return `${impact.visibleAfterPublish} visible after publish, ${impact.hiddenAfterPublish} hidden.`
  }

  if (!impact.bucketDetailsAvailable) {
    return 'Website visibility change buckets are not available for this preview.'
  }

  return `${impact.addedOnline} made visible, ${impact.removedOnline} hidden, ${impact.unchangedVisibility} unchanged.`
}

function hasFallbackGroupMetric(summary: PublishObservabilitySummaryWithLegacyImpact | null | undefined): boolean {
  return summary?.vehicleGroupBuckets?.fallbackGroupCount !== undefined
    || summary?.vehicleGroupBuckets?.fallbackGroup !== undefined
    || summary?.vehicleGroupBuckets?.fallback !== undefined
    || summary?.vehicleGroupingImpact?.fallbackGroupCount !== undefined
}

function inputUniqueVinCount(diagnostics: NonNullable<PublishStageSummary['diagnostics']>): number {
  return diagnostics.input?.audit?.uniqueVinCount ?? diagnostics.feed?.vehicleCount ?? 0
}

function outputUniqueVinCount(diagnostics: NonNullable<PublishStageSummary['diagnostics']>): number {
  return diagnostics.previewOutput?.audit.uniqueVinCount ?? diagnostics.process?.matchedUniqueVinCount ?? 0
}

// Manual-only reconciliation was previously derived from the incoming-feed
// visibility impact, whose backing stage has been removed. With no source for
// that signal, these helpers return their neutral defaults so downstream
// visibility/eligibility logic falls back to the bucket/outcome data paths.
function isManualOnlyPreview(_rawPreview: PublishObservabilitySummary | null | undefined): boolean {
  return false
}

function manualOnlyReconciliationHiddenCount(
  _rawPreview: PublishObservabilitySummary | null | undefined,
): number {
  return 0
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => String(value).trim()).filter(Boolean)))
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural
}
